import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Skeleton } from "../../shared";
import {
  listBanners,
  pickBannerCopy,
  type Banner,
} from "../../../services/banners";

/**
 * Карусель промо-баннеров на главной.
 *
 * Логика:
 *  • Скроллится горизонтально (touch/mouse), scroll-snap для чёткого «клика по кадру».
 *  • Автопрокручивается каждые 5 сек (пауза при взаимодействии).
 *  • Индикаторы-точки внизу, кликабельны — переходим к нужному кадру.
 *  • Клик по баннеру → внутренний navigate() либо `openLink` для внешних https.
 *
 * Никаких внешних зависимостей (swiper/embla) — своя реализация на CSS-scroll-snap.
 */

const AUTOPLAY_MS = 5000;

export function BannersCarousel() {
  const navigate = useNavigate();
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [pauseAutoplay, setPauseAutoplay] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryFn: listBanners,
    queryKey: ["banners"],
    staleTime: 60_000,
    retry: 1,
  });

  const banners = useMemo(() => data ?? [], [data]);

  // Определяем активный слайд по scrollLeft.
  const handleScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (w === 0) return;
    const idx = Math.round(el.scrollLeft / w);
    setActiveIdx((prev) => (prev === idx ? prev : idx));
  }, []);

  const scrollToIndex = useCallback((idx: number, smooth = true) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({
      left: idx * el.clientWidth,
      behavior: smooth ? "smooth" : "auto",
    });
  }, []);

  // Autoplay: перелистываем каждые AUTOPLAY_MS, если баннеров > 1
  // и пользователь не взаимодействует.
  useEffect(() => {
    if (banners.length <= 1 || pauseAutoplay) return;
    const id = window.setInterval(() => {
      const el = trackRef.current;
      if (!el) return;
      const next = (activeIdx + 1) % banners.length;
      el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [activeIdx, banners.length, pauseAutoplay]);

  // Клик по баннеру → навигация.
  const openBanner = (b: Banner) => {
    const url = b.link_url?.trim();
    if (!url) return;

    // Внешняя https-ссылка — открываем через Telegram Web App.
    if (/^https?:\/\//i.test(url)) {
      try {
        Telegram.WebApp.openLink(url);
      } catch {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      return;
    }

    // Внутренний путь — react-router navigate.
    navigate(url.startsWith("/") ? url : `/${url}`);
  };

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={112}
        sx={{ borderRadius: "20px" }}
      />
    );
  }

  // Ничего не рендерим, если баннеров нет или API упал.
  if (isError || banners.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{ position: "relative" }}
      onPointerDown={() => setPauseAutoplay(true)}
      onPointerUp={() => setPauseAutoplay(false)}
      onPointerLeave={() => setPauseAutoplay(false)}
    >
      {/* Slider track */}
      <Box
        ref={trackRef}
        onScroll={handleScroll}
        sx={{
          display: "flex",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          borderRadius: "20px",
          // прячем скролл-бар
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {banners.map((b) => (
          <BannerCard key={b.id} banner={b} onClick={() => openBanner(b)} />
        ))}
      </Box>

      {/*
       * Dots. Поверх карусели внизу — как на референсе.
       * pointer-events: box сам не ловит клики, а точки — ловят.
       */}
      {banners.length > 1 && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 10,
            display: "flex",
            justifyContent: "center",
            gap: "6px",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          {banners.map((b, i) => (
            <ButtonBase
              key={b.id}
              onClick={() => scrollToIndex(i)}
              aria-label={`Слайд ${i + 1}`}
              sx={{
                width: activeIdx === i ? 22 : 6,
                height: 6,
                borderRadius: "999px",
                bgcolor:
                  activeIdx === i
                    ? "#fff"
                    : "rgba(255,255,255,0.45)",
                boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                pointerEvents: "auto",
                transition: "width 200ms ease, background 200ms ease",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------
// Отдельная карточка баннера.
// ---------------------------------------------------------------

interface BannerCardProps {
  banner: Banner;
  onClick: () => void;
}

function BannerCard({ banner, onClick }: BannerCardProps) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  // Локализованные тексты баннера: под текущий язык i18n. Если админ
  // не заполнил английский вариант — fallback на русский (см. `pickBannerCopy`).
  const copy = useMemo(
    () => pickBannerCopy(banner, i18n.language),
    [banner, i18n.language],
  );
  const hasCta = Boolean(copy.ctaLabel && banner.link_url);
  const hasText = Boolean(copy.title || copy.subtitle);
  const isDark = theme.palette.mode === "dark";
  // В тёмной теме показываем отдельную картинку, если админ её задал
  // в /banners — иначе fallback на основную (light-версию).
  const imageSrc =
    isDark && banner.image_url_dark ? banner.image_url_dark : banner.image_url;
  // Градиент-маска нужен только в тёмной теме — там баннер обычно с
  // белым текстом на тёмной картинке, и без затемнения слева текст
  // может сливаться. В светлой теме админ рисует баннер сам под свой
  // светлый фон, градиент выглядит как грязное пятно.
  const showGradient = isDark && hasText;

  return (
    <ButtonBase
      onClick={onClick}
      disabled={!banner.link_url}
      // Убираем стандартную браузерную focus-обводку (синяя рамка,
      // которую Chromium рисует на MUI ButtonBase при tab-фокусе или
      // после клика внутри карусели). У баннера нет собственного focus
      // state в дизайне.
      disableRipple
      focusRipple={false}
      sx={{
        flex: "0 0 100%",
        scrollSnapAlign: "start",
        display: "block",
        textAlign: "left",
        borderRadius: "20px",
        overflow: "hidden",
        position: "relative",
        // Высоту баннера задаёт сама картинка (см. <img> ниже) —
        // блок «подстраивается» под аспект исходника.
        // Фон — цвет-подложка из БД либо тёмный дефолт.
        bgcolor: banner.bg_color || "#2C6BFF",
        color: "#fff",
        // Никаких обводок — админ рисует баннер сам, и любая рамка
        // (даже rgba(0,0,0,0.05)) в WebView Telegram’а часто
        // отдаёт синевой. Также перекрываем focus-outline браузера.
        border: "none",
        outline: "none",
        WebkitTapHighlightColor: "transparent",
        transition: "transform 120ms ease",
        "&:focus, &:focus-visible, &.Mui-focusVisible": {
          outline: "none",
          boxShadow: "none",
        },
        "&:active": {
          transform: banner.link_url ? "scale(0.985)" : "none",
        },
      }}
    >
      {/*
       * Картинка формирует высоту баннера.
       *  • width: 100% → всегда на всю ширину карусели
       *  • height: auto → высота = ширина × (h/w картинки)
       *  • Пропорции ВСЕГДА сохраняются, ничего не обрезаем и не тянем.
       * Все текст / CTA / точки-индикаторы — оверлеями поверх.
       */}
      <Box
        component="img"
        src={imageSrc}
        alt={copy.title ?? ""}
        loading="lazy"
        draggable={false}
        sx={{
          display: "block",
          width: "100%",
          height: "auto",
          userSelect: "none",
          pointerEvents: "none",
        }}
      />

      {/*
       * Лёгкая градиентная маска слева — только для тёмной темы, где
       * белый текст надо читать поверх тёмной картинки. В светлой
       * теме её не показываем (админ сам рисует баннер под свой фон,
       * градиент выглядит как «грязное» пятно — см. правку от юзера).
       */}
      {showGradient && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0) 100%)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Контент. В светлой теме используем тёмный текст (белый на светлой
          картинке плохо читается, а тень выглядит грязно). В тёмной —
          белый с deep-shadow как раньше. */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          padding: "16px 18px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          alignItems: "flex-start",
          justifyContent: "center",
          maxWidth: "65%",
        }}
      >
        {copy.title && (
          <Typography
            sx={{
              fontSize: "17px",
              fontWeight: 700,
              lineHeight: 1.15,
              color: isDark ? "#fff" : "#1D1D1F",
              textShadow: isDark ? "0 1px 6px rgba(0,0,0,0.35)" : "none",
            }}
          >
            {copy.title}
          </Typography>
        )}
        {copy.subtitle && (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: 1.3,
              color: isDark
                ? "rgba(255,255,255,0.85)"
                : "rgba(60, 60, 67, 0.7)",
              textShadow: isDark ? "0 1px 6px rgba(0,0,0,0.35)" : "none",
            }}
          >
            {copy.subtitle}
          </Typography>
        )}
        {hasCta && (
          <Box
            sx={{
              marginTop: "6px",
              padding: "6px 12px",
              borderRadius: "999px",
              // CTA-плашка: в тёмной — полупрозрачный белый с blur,
              // в светлой — тёмная plашка под белый текст (высокий
              // контраст на любой картинке).
              bgcolor: isDark
                ? "rgba(255,255,255,0.18)"
                : "rgba(29, 29, 31, 0.85)",
              backdropFilter: "blur(6px)",
              fontSize: "12px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              color: "#fff",
            }}
          >
            {copy.ctaLabel}
            <Icon icon="solar:alt-arrow-right-linear" width={14} height={14} />
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
}
